import eventRefs from "../../assets/eventRef";
import refs from "../../assets/refs";
import { DEVICE_CONNECTION_EVENT_CODE } from "../../utils/eventLogKinds";

const DEVICE_SOURCE = "device";
const CLOUD_SOURCE = "cloud";

const DEFAULT_CLOUD_NOTE = {
  title: "Source",
  description:
    "This event is generated by Synaccess Cloud from device reporting state. It is documented here, but it is not selectable in the device alert editor.",
};

const SECTION_METADATA = {
  "Network Events": {
    id: "network-routing",
    title: "Network & Routing",
    icon: "lan",
    summary:
      "Track reachability checks and uplink path changes that affect device connectivity or downstream services.",
    applicability: ["Cloud-connected PDUs"],
  },
  "Inlet Monitoring Events": {
    id: "inlet-monitoring",
    title: "Inlet Monitoring",
    icon: "electrical_services",
    summary:
      "Watch inlet current, voltage, frequency, power, and energy thresholds at the source feeding the device.",
    applicability: ["Single-phase and three-phase models vary by trigger"],
  },
  "Bank Monitoring Events": {
    id: "bank-monitoring",
    title: "Bank Monitoring",
    icon: "power",
    summary:
      "Detect bank-level load conditions, breaker trips, and power quality changes across monitored circuits.",
    applicability: ["Bank-aware PDUs"],
  },
  "Outlet Monitoring Events": {
    id: "outlet-monitoring",
    title: "Outlet Monitoring",
    icon: "outlet",
    summary:
      "Alert on outlet current conditions for metered outlets without leaving the alert editor.",
    applicability: ["Switched and metered outlet models"],
  },
  "Temperature & Humidity Sensor Events": {
    id: "environmental-sensors",
    title: "Environmental Sensors",
    icon: "device_thermostat",
    summary:
      "Use attached sensors to watch rack temperature and humidity before equipment drifts out of spec.",
    applicability: ["Temperature and humidity sensor ports"],
  },
  "IO Hub Sensor Events": {
    id: "io-hub-inputs",
    title: "IO Hub Inputs",
    icon: "hub",
    summary:
      "Monitor digital inputs and user switch activity from the SPA-0140 Smart IO Hub.",
    applicability: ["SPA-0140 Smart IO Hub"],
  },
  "Sensor Events": {
    id: "sensor-state",
    title: "Sensor Connection State",
    icon: "sensors",
    summary:
      "Alert when a monitored sensor is attached or removed from a sensor port.",
    applicability: ["Port A and Port B sensor connections"],
  },
  "Scheduled/Interval Events": {
    id: "scheduled-triggers",
    title: "Scheduled Triggers",
    icon: "schedule",
    summary:
      "Run alerts on daily, weekly, monthly, or repeating intervals when you need time-based automation.",
    applicability: ["All supported PDUs"],
  },
  "Circuit Breaker Trip Events": {
    id: "breaker-trips",
    title: "Circuit Breaker Trips",
    icon: "bolt",
    summary:
      "Capture breaker trip conditions so your team can react quickly to lost power paths.",
    applicability: ["Devices with supported breaker telemetry"],
  },
  "ATS Event Events": {
    id: "ats-events",
    title: "ATS Power Source Events",
    icon: "swap_horiz",
    summary:
      "Track Automatic Transfer Switch behavior when the device moves between primary and secondary power sources.",
    applicability: ["ATS PDUs"],
  },
  cloudConnection: {
    id: "cloud-connection-events",
    title: "Cloud Connection Events",
    icon: "cloud",
    summary:
      "Understand the online and offline state changes that Synaccess Cloud records when a device stops or resumes reporting.",
    applicability: ["Cloud monitoring timeline"],
  },
};

const EVENT_METADATA = {
  11: {
    summary:
      "Detect when a remote host becomes reachable so you can run a follow-up action after recovery or startup.",
    notes: [
      {
        title: "Best for",
        description:
          "Use this when a dependent device must come online before you notify a team or switch power.",
      },
    ],
  },
  12: {
    summary:
      "Trigger after repeated ping failures when you need an alert for lost upstream connectivity.",
    notes: [
      {
        title: "Best for",
        description:
          "Use this to detect WAN or service loss before you escalate, fail over, or power cycle downstream gear.",
      },
    ],
  },
  53: {
    summary:
      "Run an alert once a day at a fixed time without needing a live measurement threshold.",
  },
  54: {
    summary:
      "Repeat an alert continuously on a fixed cadence for recurring actions or reminders.",
  },
  59: {
    summary:
      "Detect recovery after a power loss so teams know when a bank has booted back up.",
    notes: [
      {
        title: "Best for",
        description:
          "Useful for post-outage workflows such as notifying operators or sequencing downstream recovery steps.",
      },
    ],
  },
  60: {
    summary:
      "Run a scheduled alert on a specific day each month at a fixed hour and minute.",
  },
  61: {
    summary:
      "Run a scheduled alert on a specific weekday at a fixed hour and minute.",
  },
  65: {
    summary:
      "Track whether a sensor is connected or disconnected on a selected port.",
    notes: [
      {
        title: "Best for",
        description:
          "Use this when sensor presence matters as much as the readings themselves, such as environmental probes in critical racks.",
      },
    ],
  },
  66: {
    summary:
      "Alert when the default route fails over to LTE so operators can confirm backup connectivity is active.",
  },
  67: {
    summary:
      "Alert when the default route returns to Ethernet so teams know the preferred path is restored.",
  },
  [DEVICE_CONNECTION_EVENT_CODE]: {
    summary:
      "See when Synaccess Cloud marks a device offline or online based on reporting state rather than a configurable trigger on the PDU.",
    notes: [DEFAULT_CLOUD_NOTE],
    applicability: ["Cloud-generated only"],
    isConfigurable: false,
    source: CLOUD_SOURCE,
  },
};

function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeNotes(helpSections = []) {
  return (helpSections || [])
    .map((section) => {
      const title = String(section?.title || "").trim();
      const description = String(section?.desc || "").trim();

      if (!title && !description) {
        return null;
      }

      return {
        title,
        description,
      };
    })
    .filter(Boolean);
}

function getDefaultSummary(ref = {}) {
  return `Monitor ${String(ref.name || "this trigger").toLowerCase()} conditions.`;
}

function getDefaultStatusDescription(ref = {}, mode = "triggered") {
  const isTriggered = mode === "triggered";

  if (isTriggered) {
    return (
      ref.triggeredDescription ||
      "This trigger becomes active when the configured condition is met."
    );
  }

  return (
    ref.notTriggeredDescription ||
    "This trigger returns to normal when the configured condition clears."
  );
}

function buildParameters(ref = {}) {
  return (Array.isArray(ref.params) ? ref.params : []).map((param, index) => ({
    id: `${ref.code}-param-${index + 1}`,
    label: param?.label || `Parameter ${index + 1}`,
    unit: param?.unit || "",
    input: param?.input || "",
    placeholder: param?.placeholder || "",
  }));
}

function normalizeParameterLabel(label = "") {
  return String(label)
    .replace(/\bID\b/g, "")
    .replace(/\bRMS\b/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function joinWithCommas(items = []) {
  if (items.length <= 1) {
    return items[0] || "";
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function describeParameterReference(parameter = {}) {
  const label = normalizeParameterLabel(parameter.label || "parameter");

  if (parameter.input === "select") {
    return `the selected ${label}`;
  }

  return `the configured ${label}`;
}

function buildStateDescriptions(ref = {}, parameters = []) {
  const fallbackTriggered = getDefaultStatusDescription(ref, "triggered");
  const fallbackNormal = getDefaultStatusDescription(ref, "normal");

  if (!parameters.length) {
    return {
      triggeredDescription: fallbackTriggered,
      notTriggeredDescription: fallbackNormal,
    };
  }

  const parameterSummary = joinWithCommas(
    parameters.map((parameter) => describeParameterReference(parameter)),
  );

  return {
    triggeredDescription:
      `This alert activates when the device reports the configured condition using ${parameterSummary}.`,
    notTriggeredDescription:
      "This alert returns to normal when the monitored value or state no longer matches the configured condition.",
  };
}

function buildEntry(ref, sectionMeta = {}, eventMeta = {}) {
  if (!ref) {
    return null;
  }

  const isConfigurable = eventMeta.isConfigurable !== false;
  const source = eventMeta.source || (isConfigurable ? DEVICE_SOURCE : CLOUD_SOURCE);
  const parameters = buildParameters(ref);
  const notes = [
    ...(eventMeta.notes || []),
    ...normalizeNotes(ref.helpSections),
  ];
  const stateDescriptions = buildStateDescriptions(ref, parameters);

  return {
    code: Number(ref.code),
    id: `event-${ref.code}`,
    name: ref.name,
    summary: eventMeta.summary || getDefaultSummary(ref),
    parameters,
    triggeredDescription: stateDescriptions.triggeredDescription,
    notTriggeredDescription: stateDescriptions.notTriggeredDescription,
    applicability:
      eventMeta.applicability || sectionMeta.applicability || [],
    notes,
    isConfigurable,
    source,
    sourceLabel:
      source === CLOUD_SOURCE ? "Cloud-generated" : "Configurable on device",
  };
}

function buildSection(sectionConfig = {}, eventMap = new Map()) {
  const meta =
    SECTION_METADATA[sectionConfig.categoryLabel] || sectionConfig.meta || {};
  const entries = (sectionConfig.codes || [])
    .map((code) =>
      buildEntry(
        eventMap.get(Number(code)),
        meta,
        EVENT_METADATA[Number(code)] || {},
      ),
    )
    .filter(Boolean);

  if (entries.length === 0) {
    return null;
  }

  return {
    id: meta.id || slugify(meta.title || sectionConfig.categoryLabel || "alerts"),
    title: meta.title || sectionConfig.categoryLabel,
    icon: meta.icon || "notifications",
    summary: meta.summary || "",
    applicability: meta.applicability || [],
    entries,
  };
}

export function getDocumentedSelectableCodes() {
  return Array.from(
    new Set(
      (refs.eventCategories || [])
        .flatMap((category) => category.codes || [])
        .map((code) => Number(code))
        .filter(Number.isFinite),
    ),
  );
}

export function buildAlertDocumentationModel() {
  const eventMap = new Map(
    (eventRefs || []).map((ref) => [Number(ref.code), ref]),
  );

  const sections = (refs.eventCategories || [])
    .map((category) =>
      buildSection(
        {
          categoryLabel: category.label,
          codes: category.codes,
        },
        eventMap,
      ),
    )
    .filter(Boolean);

  const cloudSection = buildSection(
    {
      categoryLabel: "cloudConnection",
      meta: SECTION_METADATA.cloudConnection,
      codes: [DEVICE_CONNECTION_EVENT_CODE],
    },
    eventMap,
  );

  if (cloudSection) {
    sections.push(cloudSection);
  }

  const entries = sections.flatMap((section) => section.entries || []);
  const configurableEntries = entries.filter((entry) => entry.isConfigurable);
  const cloudEntries = entries.filter((entry) => !entry.isConfigurable);

  return {
    sections,
    stats: {
      totalEvents: entries.length,
      configurableCount: configurableEntries.length,
      cloudCount: cloudEntries.length,
      sectionCount: sections.length,
    },
  };
}

export const alertDocumentationModel = buildAlertDocumentationModel();
